import { singleton, inject } from 'tsyringe';
import { PersistentStorageService } from '../persistent-storage-service/persistent-storage.service';
import { RecommendationConditions } from './models/conditions.model';
import {
  HostExtensionConfigurations,
  RawGlobalConfigurations,
} from './models/raw-config.model';
import { RawGame } from './models/raw-game.model';
import { RecommendationConfig } from './models/recommendation-config.model';
import { RemoteObjectRetrieverService } from '../remote-object/remote-object-retriever.service';
import gameTriggers from '../../../../externals/config/game-event-triggers.json';
import { LoggerService } from '../logger.service';

export const kDITRecommendationRemoteObjectRetrieverService =
  'rec-remote-object-ret-service';

type RemoteObjectService = RemoteObjectRetrieverService<RecommendationConfig>;

// -----------------------------------------------------------------------------
// Acts as a convenience wrapper around the raw remote configuration json.
//
// Keep this class as dumb as possible and do not convert between the raw models
// to the shared models (e.g. RawGame --> Game).
// We already have |RecommendationDatastoreService|
@singleton()
export class RemoteConfigurationsService {
  private readonly logger = LoggerService.getCategory(
    'RemoteConfigurationsService',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kDITRecommendationRemoteObjectRetrieverService)
    private readonly remoteObjectRetrieverService: RemoteObjectService,
    private readonly persistentStorage: PersistentStorageService,
  ) {}

  // ---------------------------------------------------------------------------
  public async getRawGame(gameId: number): Promise<RawGame> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.games[gameId];
  }

  // ---------------------------------------------------------------------------
  public async setNewEnvironment(
    environment: string,
    replacementString = 'prod',
  ): Promise<boolean> {
    await this.remoteObjectRetrieverService.initConfigUrl(
      this.remoteObjectRetrieverService.options.configUrl.replace(
        replacementString,
        environment,
      ),
    );

    return true;
  }

  // ---------------------------------------------------------------------------
  public async getRawGames(): Promise<{ [key: number]: RawGame }> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.games;
  }

  // ---------------------------------------------------------------------------
  public async getRawGlobalConfigs(): Promise<RawGlobalConfigurations> {
    const remoteConfigs = await this.getRemoteConfigs();
    return remoteConfigs?.config;
  }

  // ---------------------------------------------------------------------------
  public async getAppName(id: number): Promise<string> {
    const remoteConfigs = await this.getRemoteConfigs();
    const appNames = remoteConfigs?.lookups?.app_names;
    if (!appNames) {
      return null;
    }

    return appNames[id];
  }

  // ---------------------------------------------------------------------------
  public async getAppUid(id: number): Promise<string> {
    const remoteConfigs = await this.getRemoteConfigs();
    const appUids = remoteConfigs?.lookups?.apps;
    if (!appUids) {
      return null;
    }

    return appUids[id];
  }

  // ---------------------------------------------------------------------------
  // CR(twolf): Consider returning |RecommendationCondtions[]|
  public async getSpecificConditions(applicationId: number): Promise<number[]> {
    const remoteConfigs = await this.getRemoteConfigs();
    const specificConditions =
      remoteConfigs.app_conditions.specific[applicationId];

    return specificConditions;
  }

  // ---------------------------------------------------------------------------
  public async getCondition(id: number): Promise<RecommendationConditions> {
    const remoteConfigs = await this.getRemoteConfigs();
    const conditions = remoteConfigs?.lookups?.conditions;
    if (!conditions) {
      return null;
    }

    return conditions[id];
  }

  // ---------------------------------------------------------------------------
  // CR(twolf): I'd return |ExtensionId[]| here - it is "raw" enough for this
  // class IMO
  public async getBlacklistExtensionIds(
    applicationId: number,
  ): Promise<number[]> {
    const remoteConfigs = await this.getRemoteConfigs();
    const blacklistedExtensionIds = remoteConfigs.blacklisted_apps;
    if (!blacklistedExtensionIds) {
      return null;
    }

    return blacklistedExtensionIds[applicationId];
  }

  // ---------------------------------------------------------------------------
  public async getBlacklistAppLookup(blacklistId: number): Promise<string> {
    const remoteConfigs = await this.getRemoteConfigs();
    const appLookup = remoteConfigs.lookups.apps;
    if (!appLookup) {
      return null;
    }

    return appLookup[blacklistId];
  }

  // ---------------------------------------------------------------------------
  public async getHostApps() {
    const remoteConfigs = await this.getRemoteConfigs();
    const rawHosts = remoteConfigs.hosts;
    if (!rawHosts) {
      return null;
    }

    const hosts = {};

    const keys = Object.keys(rawHosts);
    for (const hostKey of keys) {
      const extensionId = await this.getAppUid(parseInt(hostKey));
      if (!hosts[extensionId]) {
        hosts[extensionId] = [];
      }

      const rawHostApp = rawHosts[hostKey];

      for (const hostApp of rawHostApp) {
        const uid = await this.getAppUid(hostApp.id);
        hosts[extensionId].push({
          id: uid,
          phasing: hostApp.phasing,
          enabled: hostApp.enabled,
        } as HostExtensionConfigurations);
      }
    }

    return hosts;
  }

  // ---------------------------------------------------------------------------
  private async getRemoteConfigs() {
    let remoteConfigs = await this.remoteObjectRetrieverService.getObject();
    if (!remoteConfigs) {
      return this.persistentStorage.getGlobalConfiguration();
    }

    remoteConfigs = this.appendGameEventTriggers(remoteConfigs);
    this.persistentStorage.setGlobalConfiguration(remoteConfigs);
    return remoteConfigs;
  }

  // ---------------------------------------------------------------------------
  private appendGameEventTriggers(remoteConfigs: RecommendationConfig) {
    const apps = remoteConfigs.lookups?.apps;
    for (const gameId in gameTriggers) {
      if (!Object.prototype.hasOwnProperty.call(gameTriggers, gameId)) {
        continue;
      }

      const game = remoteConfigs.games[gameId];
      for (const app of game?.apps ?? []) {
        const appUid = apps[app.id];
        if (!gameTriggers[gameId][appUid]) {
          continue;
        }

        app.game_events_trigger = gameTriggers[gameId][appUid];
      }
    }

    return remoteConfigs;
  }
}
